import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { map, catchError, finalize, switchMap } from 'rxjs/operators';

import { NavController } from '@ionic/angular';

import { LoadingService } from '../loading-service/loading.service';
import { ILocalAppEnvironment } from '../environment-config-service/environment-config.service';

import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Service classes are no longer generated - using function-based APIs instead
// Import PATH constants from function files
import {
  localAccountRefreshTokenPost,
  localAccountRegisterUserPost,
  localAccountLoginUserPost,
  localAccountRequestOtpPost,
  localAccountVerifyOtpAsyncPost,
  localAccountForgotPasswordRequestPost,
  localAccountResetForgotPasswordPost
} from 'src/app/api/proxy/auth/functions';
import {
  appEnvironmentMyCountryPost,
  appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet,
  appEnvironmentCountriesGet,
  appEnvironmentCountriesForUserDeviceTypeAppIdProductionGet,
  appEnvironmentSettingsPost,
  appEnvironmentGetVivaScoreAsyncPost,
  appEnvironmentVivaScorePingPost
} from 'src/app/api/proxy/app-environment/functions';
import {
  consumerGetByTokenTokenGet,
  consumerGetByTokenPartOfOrganizationIdTokenGet,
} from 'src/app/api/proxy/organization/functions';
import { consumerValidateIdentifierPerOrganizationOrganizationIdGet } from 'src/app/api/proxy/mpi/functions';
import {
  sessionRequestSessionPost,
  sessionSessionResultSessionIdGet,
  sessionSdkScanConfigAsyncPost,
  sessionSubmitSdkSessionPost,
  sessionAccessCodeConfigGetSystemIdentifierIdAccessCodeGet
} from 'src/app/api/proxy/facescan/functions';
import {
  sessionRequestSessionPost as vivaliteSessionRequestSessionPost,
  sessionSessionResultSessionIdGet as vivaliteSessionSessionResultSessionIdGet,
  sessionSdkScanConfigAsyncPost as vivaliteSessionSdkScanConfigAsyncPost,
  sessionSubmitSdkSessionPost as vivaliteSessionSubmitSdkSessionPost,
  sessionAccessCodeConfigGetSystemIdentifierIdAccessCodeGet as vivaliteSessionAccessCodeConfigGetSystemIdentifierIdAccessCodeGet,
  sessionAccessCodeIdentifierAllowedSystemIdentifierIdAccessCodePost as vivaliteSessionAccessCodeIdentifierAllowedSystemIdentifierIdAccessCodePost,
  sessionSubmitWebSdkSessionPost as vivaliteSessionSubmitWebSdkSessionPost,
  sessionSdkMessageLogPost as vivaliteSessionSdkMessageLogPost,
  sessionSdkCompletedPost as vivaliteSessionSdkCompletedPost,
  sessionValidateScanAllowedSessionIdGet as vivaliteSessionValidateScanAllowedSessionIdGet
} from 'src/app/api/proxy/vivalite-scan/functions';

import { EventService } from '../events/event.service';
import { TokenService } from '../token/token.service';
import { UserData } from './user-data.model';
import { sharedConfig } from 'src/environments/shared-config';
import { LocaleService } from '../locale-service/locale.service';

@Injectable()
export class GlobalHttpInterceptorService implements HttpInterceptor {
  private appInfo?: AppInfo | null = null;
  private environment: ILocalAppEnvironment | null = null;
  private currentUser: UserData | null = null;

  // refresh control
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(
    public navCtrl: NavController,
    private _TokenService: TokenService,
    private _localeService: LocaleService,
  ) {
    EventService.environmentLoaded.subscribe((env: ILocalAppEnvironment) => {
      this.environment = env;
    });

    EventService.userUpdated.subscribe((usr: UserData | null) => {
      this.currentUser = usr;
    });
  }

  // ───────────────────────────── intercept ─────────────────────────────

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1) fully bypass for ignored endpoints
    if (this.isIgnoredUrl(req.url)) {
      return next.handle(req);
    }

    // 2) short-circuit for public endpoints (no auth, no refresh)
    if (this.isPublicUrl(req.url)) {
      return from(this.addHeaders(req, null /*no token*/, true /*public*/))
        .pipe(switchMap((cloned) => this.handleRequest(cloned, next)));
    }

    // 3) private endpoint: try to get a token; if none, send without and let it fail
    return from(this.buildPrivateRequest(req)).pipe(
      switchMap(({ request, hadAuth }) =>
        this.handleRequest(request, next, hadAuth)
      )
    );
  }

  // ─────────────────────────── helpers/core ───────────────────────────

  /** Wraps the actual HTTP call with loader + 401 handling. */
  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    hadAuth = false
  ): Observable<HttpEvent<any>> {
    LoadingService.setLoading(true, request.url);

    return next.handle(request).pipe(
      map((evt) => {
        if (evt instanceof HttpResponse) {
          // success path hook if needed
        }
        return evt as HttpEvent<any>;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // If we never had Authorization on this request (no session), skip refresh flow.
          if (!hadAuth) {
            return throwError(() => error);
          }
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        LoadingService.setLoading(false, request.url);
      })
    );
  }

  /** Constructs a private request. Adds Authorization if available; otherwise none. */
  private async buildPrivateRequest(req: HttpRequest<any>): Promise<{ request: HttpRequest<any>; hadAuth: boolean; }> {
    // try to get a token (do NOT force-refresh here)
    let token: string | null = null;
    try {
      token = await this._TokenService.getTokenAsync();
    } catch {
      token = null;
    }

    // if no token yet, still proceed without auth (as you requested)
    const hadAuth = !!token;
    const cloned = await this.addHeaders(req, token, false /*public*/);
    return { request: cloned, hadAuth };
  }

  // ─────────────────────────── 401 handling ───────────────────────────

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // do not refresh if this is the refresh endpoint itself (avoid loops)
    const refreshPath = this.extractUrlNoParams(localAccountRefreshTokenPost.PATH);
    if (request.url.includes(refreshPath)) {
      return next.handle(request)
    }

    // If we have no user/session, do not attempt refresh.
    if (!this.currentUser) {
      return next.handle(request);
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshPromise = this._TokenService.refreshTokenAsync();

      if (!this.refreshPromise) {
        this.isRefreshing = false;
        return throwError(() => new Error('No refresh promise created'));
      }

      return from(this.refreshPromise).pipe(
        switchMap((newToken) => {
          this.isRefreshing = false;
          this.refreshPromise = null;

          if (!newToken) {
            return throwError(() => new Error('Token refresh returned null'));
          }

          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned))
          );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshPromise = null;
          return throwError(() => err);
        })
      );
    } else {
      // coalesce concurrent 401s while a refresh is in progress
      return from(this.refreshPromise!).pipe(
        switchMap((newToken) => {
          if (!newToken) {
            return throwError(() => new Error('Coalesced refresh returned null'));
          }
          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned))
          );
        }),
        catchError((err) => throwError(() => err))
      );
    }
  }

  // ───────────────────────── headers & utils ─────────────────────────

  /** Adds headers. If `isPublic`, never adds Authorization. */
  private async addHeaders(
    request: HttpRequest<any>,
    token: string | null,
    isPublic: boolean
  ): Promise<HttpRequest<any>> {
    // default headers — BCP-47 culture for .NET RequestLocalization
    const acceptLanguage = this._localeService.getAcceptLanguageHeader();
    let headers = request.headers
      .set('Accept-Language', acceptLanguage)
      .set('ngrok-skip-browser-warning', 'true');

    // app info (cached)
    if (!this.appInfo) {
      this.appInfo = (Capacitor.getPlatform() !== 'web')
        ? await App.getInfo()
        : {
            name: window.location.hostname,
            id: sharedConfig.webAppId!,
            version: sharedConfig.version,
            build: sharedConfig.build.toString(),
          };
    }

    // app/env headers (safe for both public & private)
    if (this.appInfo && this.environment?.countryCode) {
      const tzOffsetMinutes = -new Date().getTimezoneOffset();
      const tzOffsetMinutesStr = `${tzOffsetMinutes >= 0 ? '+' : ''}${tzOffsetMinutes}`;
      headers = headers
        .set('ion-app-deviceType', Capacitor.getPlatform())
        .set('ion-app-name', this.appInfo.name)
        .set('ion-app-id', this.appInfo.id)
        .set('ion-app-version', this.appInfo.version)
        .set('ion-app-build', this.appInfo.build)
        .set('ion-app-countryCode', this.environment.countryCode)
        .set('ion-app-timezoneoffsetminutes', tzOffsetMinutesStr);
    }

    // correlation (only if we have a user)
    if (this.currentUser?.tokenClaims?.personId) {
      const personVal = btoa(`${this.currentUser.sub}|${this.currentUser.tokenClaims.personId}`);
      headers = headers.set('correlation', personVal);
    }

    // FaceScan special header (applies to both public/private if URL matches)
    if (
      this.environment?.faceScanConfig?.faceScanBase &&
      request.url.includes(this.environment.faceScanConfig.faceScanBase)
    ) {
      headers = headers.set('healthcheck-systemIdentifier-id', this.environment.faceScanConfig.systemIdentifierId!);
    }

    // Authorization only on private requests *and* only if a token was provided
    if (!isPublic && token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return request.clone({ headers });
  }

  // Public endpoints (no auth, no refresh)
  private isPublicUrl(url: string): boolean {
    const pub = [
      // AppEnvironment bootstrap
      this.extractUrlNoParams(appEnvironmentMyCountryPost.PATH),
      this.extractUrlNoParams(appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet.PATH),
      this.extractUrlNoParams(appEnvironmentCountriesGet.PATH),
      this.extractUrlNoParams(appEnvironmentCountriesForUserDeviceTypeAppIdProductionGet.PATH),
      this.extractUrlNoParams(appEnvironmentSettingsPost.PATH),
      this.extractUrlNoParams(appEnvironmentGetVivaScoreAsyncPost.PATH),
      this.extractUrlNoParams(appEnvironmentVivaScorePingPost.PATH),

      // Auth (public/session-creating)
      this.extractUrlNoParams(localAccountRegisterUserPost.PATH),
      this.extractUrlNoParams(localAccountLoginUserPost.PATH),
      this.extractUrlNoParams(localAccountRequestOtpPost.PATH),
      this.extractUrlNoParams(localAccountVerifyOtpAsyncPost.PATH),
      this.extractUrlNoParams(localAccountForgotPasswordRequestPost.PATH),
      this.extractUrlNoParams(localAccountResetForgotPasswordPost.PATH),

      // Organization "by token"
      this.extractUrlNoParams(consumerGetByTokenTokenGet.PATH),
      this.extractUrlNoParams(consumerGetByTokenPartOfOrganizationIdTokenGet.PATH),

      // MPI anonymous identifier validation (registration)
      this.extractUrlNoParams(consumerValidateIdentifierPerOrganizationOrganizationIdGet.PATH),

      // FaceScan bootstrap endpoints
      this.extractUrlNoParams(sessionRequestSessionPost.PATH),
      this.extractUrlNoParams(sessionSessionResultSessionIdGet.PATH),
      this.extractUrlNoParams(sessionSdkScanConfigAsyncPost.PATH),
      this.extractUrlNoParams(sessionSubmitSdkSessionPost.PATH),
      this.extractUrlNoParams(sessionAccessCodeConfigGetSystemIdentifierIdAccessCodeGet.PATH),

      // VivaLite bootstrap endpoints
      this.extractUrlNoParams(vivaliteSessionRequestSessionPost.PATH),
      this.extractUrlNoParams(vivaliteSessionSessionResultSessionIdGet.PATH),
      this.extractUrlNoParams(vivaliteSessionSdkScanConfigAsyncPost.PATH),
      this.extractUrlNoParams(vivaliteSessionSubmitSdkSessionPost.PATH),
      this.extractUrlNoParams(vivaliteSessionAccessCodeConfigGetSystemIdentifierIdAccessCodeGet.PATH),
      this.extractUrlNoParams(vivaliteSessionAccessCodeIdentifierAllowedSystemIdentifierIdAccessCodePost.PATH),
      this.extractUrlNoParams(vivaliteSessionSubmitWebSdkSessionPost.PATH),
      this.extractUrlNoParams(vivaliteSessionSdkMessageLogPost.PATH),
      this.extractUrlNoParams(vivaliteSessionSdkCompletedPost.PATH),
      this.extractUrlNoParams(vivaliteSessionValidateScanAllowedSessionIdGet.PATH),

      // Refresh endpoint itself (public handling avoids loops; we also guard in 401 path)
      this.extractUrlNoParams(localAccountRefreshTokenPost.PATH),
    ];
    return pub.some((p) => url.includes(p));
  }

  // Fully ignored (bypass interceptor entirely)
  private isIgnoredUrl(url: string): boolean {
    const ignored = [
      '/AppLogMessage',
      '/i18n/',
      'https://api.onesignal.com/',
    ];
    return ignored.some((p) => url.includes(p));
  }

  private extractUrlNoParams(url: string): string {
    const idx = url.indexOf('{');
    return idx === -1 ? url : url.substring(0, idx);
  }
}